// src/i18n/es/insightV2.ts
// Shared chassis text for the five w7.u5 screens (reconciliation, control,
// status, benchmark, fit): the property selector and the coverage/freshness
// stamp. Metric names are translated here by the metric id (`metrics`); the
// API's Spanish `label` is only a fallback. The remedy sentences are stated here once —
// verbatim from 02-metric-catalogue.md S1.4, the same text w7.u2's own
// Operación page shows, so the same code reads the same sentence everywhere.
import metricLabels from './metricLabels';

export const insightV2 = {
  // Metric names by the API's stable id (`resources.insightV2.metrics.<id>`).
  metrics: metricLabels,

  property: {
    label: 'Hotel',
    placeholder: 'Elija un hotel',
  },

  stamp: {
    updated: 'Actualizado %{relative}',
    sample: 'Muestra: %{count}',
    insufficientSample: 'Muestra insuficiente: %{count} caso en el periodo (mínimo 20). |||| Muestra insuficiente: %{count} casos en el periodo (mínimo 20).',
  },

  // What a v2 screen says when the user has no hotel with check-in data:
  // no access to any, or access only to hotels that send none.
  noCheckinData: {
    message: 'Ninguno de sus hoteles tiene todavía datos de check-in.',
    hint: 'Si necesita consultar otro hotel, solicite acceso al administrador de su cuenta.',
  },

  remedies: {
    R_FEED: 'Dato no disponible: este hotel no recibe sus reservas de su sistema de reservas. Active la entrega de reservas →',
    R_OUTBOX: 'No disponible: la entrega de eventos de este dispositivo no es fiable todavía. Actualice el software del kiosco →',
    R_N: 'Muestra insuficiente: menos de 20 casos en el periodo.',
    R_PENDING: 'Pendiente de confirmación del sistema de reservas del hotel.',
    R_EVENTTIME: 'No disponible con la conexión actual: el sistema de reservas de este hotel se consulta periódicamente y no informa la hora de sus eventos.',
    R_PMSFIELD: 'El sistema de reservas de este hotel no informa este dato.',
    R_SIGNAL: 'Sin señal desde hace %{minutes} min.',
    R_MARKER: 'Sin referencia de cadena: menos de tres hoteles con dato.',
    R_NO_BUSINESS_DAY_ROLLOVER: 'Día de negocio no configurado: este hotel no tiene definida la hora de cierre del día.',
    R_HOTEL_SOURCE:
      'No disponible: falta el registro de cobros del sistema del hotel con el que contrastar lo que declara el punto de autoservicio.',
  },

  // Human names for the words the v2 API states inside a breakdown (step
  // kinds, outcomes, reasons, channels, key carriers, payment purposes...),
  // 01-domain-model.md §3. One flat list: each word reads the same wherever
  // it appears. A word missing here is shown in a readable form, never raw.
  terms: {
    // Step kinds (§3.4)
    identify: 'Identificación',
    reservation_confirm: 'Confirmación de la reserva',
    room_select: 'Elección de habitación',
    party_capture: 'Datos de los huéspedes',
    document_capture: 'Lectura del documento',
    document_verify: 'Verificación del documento',
    signature: 'Firma',
    terms: 'Condiciones',
    upsell: 'Extras',
    tourist_tax: 'Tasa turística',
    payment: 'Pago',
    key_delivery: 'Entrega de llave',
    print: 'Impresión',
    survey: 'Encuesta',
    checkout_balance: 'Saldo de salida',
    invoice: 'Factura',
    summary: 'Resumen',
    // Step and journey outcomes (§2.3, §3.3)
    completed: 'Completados',
    skipped: 'Omitidos',
    failed: 'Fallidos',
    abandoned_here: 'Abandonados aquí',
    backtracked: 'Vueltos atrás',
    completed_without_key: 'Completados sin llave',
    handed_to_desk: 'Enviados a recepción',
    abandoned: 'Abandonados',
    expired: 'Enlace caducado',
    sin_resultado: 'Sin resultado',
    // Jobs (§3.2) and surfaces
    pre_checkin: 'Pre-check-in',
    checkin: 'Check-in',
    key_pickup: 'Recogida de llave',
    key_activation: 'Activación de llave',
    checkout: 'Check-out',
    walk_in: 'Sin reserva (walk-in)',
    purchase: 'Compras',
    ticket_access: 'Acceso con entrada',
    kiosk: 'Kiosco',
    online: 'Online',
    mobile_kiosk: 'Kiosco móvil',
    air_lobby: 'Air Lobby',
    dispenser: 'Dispensador',
    back_office: 'Recepción',
    // Reasons (§3.6, §3.8)
    pms_flag: 'Marcada para recepción en el sistema de reservas del hotel',
    staff_at_device: 'Personal en el kiosco',
    remote_control: 'Asistencia remota',
    phone_call: 'Llamada',
    unsupported_case: 'Caso no admitido en autoservicio',
    policy: 'Política del hotel',
    staff_action: 'Decisión del personal',
    idle_timeout: 'Inactividad',
    back_to_home: 'Volvió al inicio',
    cancel: 'Cancelado por el huésped',
    session_closed: 'Sesión cerrada',
    staff_reset: 'Reiniciado por el personal',
    link_closed: 'Enlace cerrado',
    sin_motivo: 'Sin motivo indicado',
    sin_codigo: 'Sin código de error',
    pre_checkin_done: 'Pre-check-in ya hecho',
    config_disabled: 'Desactivado en la configuración',
    not_applicable: 'No aplica',
    // Arrival end states
    checked_in: 'Con check-in',
    arrival_day_ended_without_checkin: 'Sin check-in al cerrar el día',
    cancelled: 'Canceladas',
    no_show: 'No presentadas',
    // Keys (§2.3)
    card: 'Tarjeta',
    wristband: 'Pulsera',
    phone: 'Móvil',
    code: 'Código',
    link: 'Enlace',
    rfid: 'RFID',
    magnetic: 'Banda magnética',
    ble: 'Bluetooth',
    nfc: 'NFC',
    none: 'Ninguna',
    initial: 'Primera llave',
    duplicate: 'Copia',
    replacement_lost: 'Sustitución por pérdida',
    room_change: 'Cambio de habitación',
    extension: 'Ampliación de estancia',
    // Errors and peripherals
    blocking: 'Bloqueante',
    non_blocking: 'No bloqueante',
    scanner: 'Escáner de documentos',
    id_scanner: 'Escáner de documentos',
    printer: 'Impresora',
    card_dispenser: 'Dispensador de tarjetas',
    card_encoder: 'Codificador de tarjetas',
    payment_terminal: 'Datáfono',
    cash_unit: 'Módulo de efectivo',
    signature_pad: 'Tableta de firma',
    qr_reader: 'Lector QR',
    camera: 'Cámara',
    pms_link: 'Conexión con el sistema de reservas',
    locks_link: 'Conexión con las cerraduras',
    payment_link: 'Conexión de pagos',
    network: 'Red',
    pms: 'Sistema de reservas',
    locks: 'Cerraduras',
    ui: 'Pantalla',
    cash: 'Efectivo',
    // Payments (§2.3)
    balance: 'Saldo de la estancia',
    deposit: 'Depósito',
    declined: 'Rechazados',
    accepted: 'Aceptados',
    orphaned: 'Huérfanos',
    // Device health (§3.7)
    up: 'En servicio',
    degraded: 'Con avisos',
    down: 'Fuera de servicio',
    maintenance: 'En mantenimiento',
    unknown: 'Sin señal',
    // Component and connection state (contract componentState) and feed kind
    ok: 'Funciona correctamente',
    fault: 'Con fallo',
    absent: 'No instalado',
    push: 'Aviso inmediato',
    scan: 'Consulta periódica',
  },

  // Names of the error codes a device or service states (contract
  // vocabularies.md §9 «Error catalogue», plus the codes the kiosk, the online
  // check-in and the dispenser emit today). A code missing here is shown as
  // `unknown`, with the code as a reference for support.
  incidentCauses: {
    peripheral: 'Fallo en: %{component}',
    peripheral_fault: 'Fallo de un periférico',
    pms_link_fault: 'Sin conexión con el sistema de reservas',
    device_blocked: 'Equipo bloqueado',
    unknown: 'Incidencia del equipo',
  },
  errorCodes: {
    SCANNER_READ_FAILED: 'Lectura del documento fallida',
    PRINTER_PAPER_OUT: 'Impresora sin papel',
    PRINTER_PAPER_LOW: 'Queda poco papel en la impresora',
    PMS_TIMEOUT: 'El sistema de reservas del hotel no respondió a tiempo',
    PMS_REJECTED: 'El sistema de reservas del hotel rechazó la operación',
    PMS_CHECKIN_FAILED: 'El sistema de reservas del hotel no completó el check-in',
    PMS_RECOVERY_FAILED: 'No se pudo recuperar el check-in',
    PMS_RESERVATION_NOT_FOUND: 'Reserva no encontrada en el sistema de reservas del hotel',
    PMS_ROOM_NOT_READY: 'Habitación no preparada',
    PMS_SEND_DOCS_FAILED: 'No se pudieron enviar los documentos al sistema de reservas del hotel',
    PMS_REVERSE_CHECKIN_FAILED: 'No se pudo anular el check-in en el sistema de reservas del hotel',
    PMS_REFUND_FAILED: 'La devolución no se registró en el sistema de reservas del hotel',
    PMS_PAYMENT_ORPHANED: 'Cobro sin reserva a la que asignarlo',
    PMS_PAYMENT_ABANDONED: 'Cobro sin reserva a la que asignarlo',
    PAYMENT_DECLINED: 'Pago denegado',
    CARD_DECLINED: 'Tarjeta denegada',
    PAYMENT_TERMINAL_OFFLINE: 'Datáfono sin conexión',
    REFUND_FAILED: 'Devolución fallida',
    KEY_ENCODE_FAILED: 'No se pudo grabar la llave',
    KEY_DISPENSE_FAILED: 'No se pudo entregar la llave',
    KD_UID_NOT_REGISTERED: 'Tarjeta no registrada',
    LOCKS_LINK_DOWN: 'Sin conexión con las cerraduras',
    LOCKS_TIMEOUT: 'Las cerraduras no respondieron a tiempo',
    CARD_READ_FAILED: 'No se pudo leer la tarjeta',
    CARD_WRITE_FAILED: 'No se pudo grabar la tarjeta',
    CARD_HOPPER_EMPTY: 'Dispensador sin tarjetas',
    CARD_STACK_EMPTY: 'Dispensador sin tarjetas',
    CARD_HOPPER_LOW: 'Quedan pocas tarjetas',
    MECH_JAM: 'Tarjeta atascada',
    DISPENSER_JAM: 'Tarjeta atascada',
    CASH_JAM: 'Atasco en el módulo de efectivo',
    NETWORK_UNREACHABLE: 'Sin conexión de red',
    NETWORK_TIMEOUT: 'La red no respondió a tiempo',
    DEVICE_FAULT: 'Avería del dispositivo',
    SYSTEM_ERROR: 'Error interno del dispositivo',
    unknown: 'Otro error (referencia %{code})',
  },

  // Names of the fields inside a composed value (a payment total, the
  // pre-check-in funnel, device availability...).
  fields: {
    count: 'Operaciones',
    amount_minor: 'Importe',
    amount: 'Importe',
    completed_pct: 'Porcentaje completado',
    up_or_degraded_pct: 'En servicio',
    unknown_pct: 'Sin señal',
    share_pct: 'Porcentaje',
    step_kind: 'Paso',
    unlinked_count: 'Sin cobro de origen',
    unlinked_amount_minor: 'Importe sin cobro de origen',
    by_currency: 'Por moneda',
    counts: 'Trayectos por resultado',
    completed_share: 'Porcentaje completado',
    up_or_degraded_share: 'En servicio',
    unknown_share: 'Sin señal',
    by_cause: 'Por causa',
    by_severity: 'Por gravedad',
    planned: 'Programadas',
    unplanned: 'No programadas',
    by_skip_reason: 'Pasos omitidos por motivo',
    contradictions: 'Contradicciones',
    minutes: 'Minutos con huecos',
    by_source: 'Por origen',
    state: 'Estado',
    pms_vendor: 'Sistema de reservas',
    feed_kind: 'Tipo de entrega',
    scan_interval_minutes: 'Intervalo de consulta (min)',
    age_seconds: 'Antigüedad',
    with_rates: 'Con datos',
    total: 'Total',
    device_id: 'Dispositivo',
    label: 'Dispositivo',
    since: 'Desde',
    peripheral_id: 'Periférico',
    kind: 'Tipo',
    level: 'Nivel',
    unit: 'Unidad',
    low_threshold: 'Umbral bajo',
    blocking: 'Bloqueante',
    subject: 'Referencia',
    fact_type: 'Dato',
    field: 'Campo',
    detected_at: 'Detectado',
    journey_id: 'Trayecto',
    pms_reservation_id: 'Reserva',
    median_seconds: 'Mediana',
    p90_seconds: '9 de cada 10',
    rate: 'Tasa',
    n: 'Casos',
  },

  breakdown: {
    operations: '%{count} operación |||| %{count} operaciones',
    step: 'Paso',
    cases: '%{count} caso |||| %{count} casos',
    ninetyOf: '9 de cada 10: %{value}',
    yes: 'Sí',
    no: 'No',
  },

  export: {
    metric: 'Indicador',
    value: 'Valor',
  },
};

export default insightV2;
